import type { CoinType } from '../cointype/CoinTypeStore';
import type { GameLabel } from '../game/types';
import type { BundleItem } from '../rewards/types';
import type { ClaimableReward } from '../ui/types';

export enum MissionTypeEnum {
  SPIN_X_TIMES = 'SPIN_X_TIMES',
  BET_SUM_OF = 'BET_SUM_OF',
  WIN_SUM_OF = 'WIN_SUM_OF',
}

// Mission types whose progress is displayed as an integer (not decimal). Monetary sums
// (WIN_SUM_OF/BET_SUM_OF) can be fractional, so they're shown with decimals — e.g. 0.4/4.
export const MissionsWithIntegerProgress: MissionTypeEnum[] = [MissionTypeEnum.SPIN_X_TIMES];

export type MissionStatus = 'active' | 'completed' | 'claimed' | 'upcoming';

export interface MissionPackageData {
  packageId: number;
  scCoin: number;
  gcCoin: number;
  amount: number;
  bundleItems: BundleItem[];
}

export interface MissionGame {
  gameId: number | null;
  gameName: string;
  gameImageUrl: string;
  providerId: number | null;
}

export interface MissionResponse {
  id: string;
  missionVersionId: string;
  missionType: MissionTypeEnum;
  goal: number;
  progress: number;
  status: MissionStatus;
  coinType: CoinType;
  order: number;
  streakPoints: number;
  timeLeft: number;
  startedAt: string | null; // ISO 8601
  completedAt: string | null; // ISO 8601
  claimedAt: string | null; // ISO 8601
  isFallback: boolean;
  packageData: MissionPackageData | null;
  game: MissionGame;
  setId: string;
}

export interface StreakMilestone {
  id: string;
  requiredPoints: number;
  coinType: CoinType;
  order: number;
  claimed: boolean;
  packageData: MissionPackageData;
  isFallback: boolean;
}

export interface StreakInfo {
  totalPoints: number;
  lastClaimedOrder: number;
  milestones: StreakMilestone[];
  expiresAt: string | null;
}

export interface MissionResetTime {
  resetTime: string;
  countryCode: string;
  territoryKey: string;
}

// The previous expired set the player must force-claim before a new one is granted.
// `missions` is the FULL chain (claimed/completed/active/upcoming), same shape as missions.
export interface UnclaimedRewardSet {
  setId: string;
  missions: MissionResponse[];
}

export interface MissionSetResponse {
  missions: MissionResponse[];
  unclaimedRewards: UnclaimedRewardSet | null;
  streak: StreakInfo | null;
  resetTime: MissionResetTime | null;
  expiresAt: string | null; // ISO 8601
  nextMissionsStartAt: string | null; // ISO 8601
  playGrandPrizeAnimation?: boolean;
}

export interface ClaimedReward {
  id: string;
  missionVersionId: string;
  packageData: MissionPackageData | null;
  isFallback: boolean;
}

export interface GetUserMissionsResponse {
  scMissions: MissionSetResponse;
  gcMissions: MissionSetResponse;
  isOnboarding: boolean;
  onboardingMissions: MissionSetResponse | null;
  gameLabel: GameLabel | null;
}

export interface XpGiftCelebration {
  boxNum: number;
  rewards: ClaimableReward[];
  play: boolean;
}

export interface StreakFly {
  id: number;
  start: { x: number; y: number };
  amount: number;
  coinType: CoinType;
}
